import { Fragment, ReactElement, ReactNode } from 'react';
import { useField } from 'react-final-form';
import type { Option } from 'options';
import { FilterChip } from './FilterChip';
import { filterChipSeparator } from './FilterChip.css';
import { humanizeName } from 'utilities';
import { EmbeddedSelect } from 'components/EmbeddedSelect';

type SelectFilterChipProps<V extends string | number> = {
  name: string;
  label?: string;
  options: Option<V>[];
  width?: number;
  isMulti?: boolean;
  isSearchable?: boolean;
  defaultValueLabel?: string;
  formatOptionLabel?: (option: Option<V>) => ReactNode;
};

const SEARCH_THRESHOLD = 5;

export function SelectFilterChip<V extends string | number = string>({
  name,
  label,
  options,
  width,
  isMulti,
  isSearchable,
  defaultValueLabel,
  formatOptionLabel,
}: SelectFilterChipProps<V>): ReactElement {
  const { input } = useField(name, { subscription: { value: true } });

  const renderOption = (o: Option<V>): ReactNode => (formatOptionLabel ? formatOptionLabel(o) : o.label);

  let active: boolean;
  let valueNode: ReactNode = null;
  if (isMulti) {
    const values: (string | number)[] = Array.isArray(input.value) ? input.value : [];
    active = values.length > 0;
    if (active) {
      const picked = options.filter((o) => values.includes(o.value));
      valueNode = formatOptionLabel
        ? picked.map((o, i) => (
            <Fragment key={o.value}>
              {i > 0 ? <span className={filterChipSeparator}>,</span> : null}
              {renderOption(o)}
            </Fragment>
          ))
        : picked.map((o) => o.label).join(', ');
    }
  } else {
    active = input.value !== undefined && input.value !== null && input.value !== '';
    if (active) {
      const selected = options.find((o) => o.value === input.value);
      valueNode = selected ? renderOption(selected) : String(input.value ?? '');
    }
  }
  if (!active && defaultValueLabel != null) {
    valueNode = defaultValueLabel;
  }

  const searchable = isSearchable ?? options.length > SEARCH_THRESHOLD;

  return (
    <FilterChip
      label={label ?? humanizeName(name)}
      active={active}
      valueNode={valueNode}
      onClear={() => input.onChange(isMulti ? [] : undefined)}
      width={width}
      flush
    >
      {(close) => (
        <EmbeddedSelect
          simple
          isMulti={isMulti}
          isSearchable={searchable}
          controlShouldRenderValue={false}
          backspaceRemovesValue={false}
          tabSelectsValue={false}
          hideSelectedOptions={false}
          isClearable={false}
          options={options}
          formatOptionLabel={formatOptionLabel}
          value={active ? input.value : isMulti ? [] : null}
          onChange={(v: unknown) => {
            input.onChange(v ?? (isMulti ? [] : undefined));
            if (!isMulti) {
              close();
            }
          }}
        />
      )}
    </FilterChip>
  );
}
